import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CloudOff, Check } from 'lucide-react';

/**
 * OfflineSyncIndicator - shows connection and sync status.
 *
 * States:
 *  - Offline: red banner fixed at top
 *  - Syncing: blue spinner + "Syncing X items..."
 *  - Pending (online): amber badge "{X} pending"
 *  - Just synced: green flash then fade (brief toast)
 *
 * Props:
 *  @param {boolean} isOnline - device connectivity state
 *  @param {boolean} syncing - whether sync is in progress
 *  @param {number} pendingSyncCount - items waiting to sync
 *  @param {Date|null} lastSyncTime - for detecting just-synced state
 */
export default function OfflineSyncIndicator({
  isOnline,
  syncing,
  pendingSyncCount,
  lastSyncTime,
}) {
  const [showSyncedToast, setShowSyncedToast] = useState(false);
  const [prevPending, setPrevPending] = useState(pendingSyncCount);

  // Detect when pending count goes from >0 to 0 (sync completed)
  useEffect(() => {
    if (prevPending > 0 && pendingSyncCount === 0 && isOnline && !syncing) {
      setShowSyncedToast(true);
      const timer = setTimeout(() => setShowSyncedToast(false), 2500);
      return () => clearTimeout(timer);
    }
    setPrevPending(pendingSyncCount);
  }, [pendingSyncCount, isOnline, syncing, prevPending]);

  // Offline banner
  if (!isOnline) {
    return (
      <div className="bg-red-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
        <WifiOff className="w-4 h-4 flex-shrink-0" />
        <span>Offline - changes will sync when connected</span>
        {pendingSyncCount > 0 && (
          <span className="ml-auto bg-red-700 rounded-full px-2 py-0.5 text-xs">
            {pendingSyncCount} queued
          </span>
        )}
      </div>
    );
  }

  // Syncing indicator
  if (syncing) {
    return (
      <div className="bg-blue-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
        <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin" />
        <span>Syncing {pendingSyncCount} item{pendingSyncCount !== 1 ? 's' : ''}...</span>
      </div>
    );
  }

  // Pending items while online
  if (pendingSyncCount > 0) {
    return (
      <div className="bg-amber-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
        <CloudOff className="w-4 h-4 flex-shrink-0" />
        <span>{pendingSyncCount} pending sync</span>
      </div>
    );
  }

  // Just-synced toast (green flash then fade)
  if (showSyncedToast) {
    return (
      <div className="bg-green-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium animate-pulse">
        <Check className="w-4 h-4 flex-shrink-0" />
        <span>All changes synced</span>
      </div>
    );
  }

  // Nothing to show
  return null;
}
